import React, { useState, useEffect, useRef } from 'react';
import { apiCall, API_ENDPOINTS } from '../../utils/api';
import './AdminChats.css';

const AdminChats = () => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(false);
  const chatListInterval = useRef(null);
  const messageInterval = useRef(null);

  // Load active chats
  const loadChats = async () => {
    try {
        const response = await apiCall(API_ENDPOINTS.CHAT_ACTIVE);
        if (response.success) {
            const formattedChats = response.data.map(chat => ({
                id: chat.id,
                userId: chat.user_id,
                userName: chat.username || `User ${chat.user_id}`,
                userEmail: chat.email || 'No Email',
                lastMessage: 'View conversation', // API doesn't return last message text yet
                timestamp: chat.updated_at || chat.created_at,
                unreadCount: 0, // API implementation dependent
                status: chat.status,
                messages: [] // Will be loaded when selected
            }));
            
            // Only update if different to avoid flickering or if list length changed
            setChats(prev => {
                const isDifferent = JSON.stringify(prev) !== JSON.stringify(formattedChats);
                return isDifferent ? formattedChats : prev;
            });
        }
    } catch (e) {
        console.error('Error loading chats:', e);
    }
  };

  useEffect(() => {
    loadChats();
    chatListInterval.current = setInterval(loadChats, 5000);
    return () => clearInterval(chatListInterval.current);
  }, []);

  // Poll messages for selected chat
  useEffect(() => {
    if (selectedChat) {
        loadMessages(selectedChat.id);
        if (messageInterval.current) clearInterval(messageInterval.current);
        messageInterval.current = setInterval(() => loadMessages(selectedChat.id), 3000);
    } else {
        if (messageInterval.current) clearInterval(messageInterval.current);
    }
    return () => {
        if (messageInterval.current) clearInterval(messageInterval.current);
    };
  }, [selectedChat?.id]);

  const loadMessages = async (chatId) => {
    try {
        const response = await apiCall(API_ENDPOINTS.CHAT_MESSAGES(chatId));
        if (response.success) {
            const formattedMessages = response.data.map(msg => ({
                id: msg.id,
                text: msg.message,
                sender: msg.sender_type,
                timestamp: msg.created_at
            })).reverse(); // DB gives limit/offset desc, traditionally chat is asc

            setSelectedChat(prev => {
                if (!prev || prev.id !== chatId) return prev;
                // Only update if messages changed
                if (JSON.stringify(prev.messages) !== JSON.stringify(formattedMessages)) {
                     return { ...prev, messages: formattedMessages };
                }
                return prev;
            });
        }
    } catch (e) {
        console.error('Error loading messages:', e);
    }
  };

  const filteredChats = chats.filter(chat =>
    chat.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedChat || sending) return;

    setSending(true);
    try {
        const response = await apiCall(API_ENDPOINTS.SEND_MESSAGE, 'POST', {
            action: 'send_message',
            chat_id: selectedChat.id,
            message: message.trim(),
            sender_type: 'admin',
            // admin_id optional or handled by session on backend ideally
        });

        if (response.success) {
            setMessage('');
            loadMessages(selectedChat.id);
        }
    } catch (e) {
        console.error('Error sending message:', e);
    } finally {
        setSending(false);
    }
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    // In real app, we would mark read here via API
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };


  return (
    <div className="admin-chats">
      <div className="admin-chats-container">
        <div className="admin-chats-sidebar">
          <div className="admin-chats-search">
            <input
              type="text"
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-chats-search-input"
            />
          </div>
          <div className="admin-chats-list">
            {filteredChats.length === 0 ? (
              <div className="admin-chats-empty">
                <p>No chats found</p>
              </div>
            ) : (
              filteredChats.map((chat) => (
                <div
                  key={chat.id}
                  className={`admin-chat-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                  onClick={() => handleSelectChat(chat)}
                >
                  <div className="admin-chat-item-avatar">
                    {chat.userName.charAt(0).toUpperCase()}
                  </div>
                  <div className="admin-chat-item-content">
                    <div className="admin-chat-item-header">
                      <h4 className="admin-chat-item-name">{chat.userName}</h4>
                      <span className="admin-chat-item-time">{formatTime(chat.timestamp)}</span>
                    </div>
                    <p className="admin-chat-item-message">{chat.lastMessage}</p>
                  </div>
                  {chat.unreadCount > 0 && (
                    <span className="admin-chat-item-badge">{chat.unreadCount}</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="admin-chats-main">
          {selectedChat ? (
            <>
              <div className="admin-chats-header">
                <div className="admin-chats-header-info">
                  <div className="admin-chats-header-avatar">
                    {selectedChat.userName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="admin-chats-header-name">{selectedChat.userName}</h3>
                    <p className="admin-chats-header-email">{selectedChat.userEmail}</p>
                  </div>
                </div>
              </div>

              <div className="admin-chats-messages">
                {selectedChat.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`admin-chat-message ${msg.sender === 'admin' ? 'admin' : 'user'}`}
                  >
                    <div className="admin-chat-message-content">
                      <p>{msg.text}</p>
                      <span className="admin-chat-message-time">
                        {new Date(msg.timestamp).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <form className="admin-chats-input-form" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="admin-chats-input"
                />
                <button type="submit" className="admin-chats-send" disabled={!message.trim()}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </form>
            </>
          ) : (
            <div className="admin-chats-placeholder">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#98a2b3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>Select a chat to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChats;

